import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import Logo from "@/components/site/Logo";
import { NAV } from "@/constants/testIds";
import { MAIN_NAV as links } from "@/constants/navigation";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { trackCtaClick } from "@/lib/ga";

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const { data: site } = useSiteSettings();
  const displayName = site?.displayName || site?.companyName || "";
  const shortName   = site?.shortName || "";
  const companyName = site?.companyName || "";
  const ariaLabel   = shortName && companyName
    ? `${shortName} — ${companyName} · home`
    : companyName || "Home";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-all ${
        scrolled
          ? "bg-white/80 backdrop-blur-xl border-b border-[#e5e5ea]"
          : "bg-white border-b border-transparent"
      }`}
    >
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 md:px-10">
        <div className="flex h-[64px] items-center gap-3 sm:gap-4">
          {/* Left: Logo mark only, on every breakpoint */}
          <Link
            to="/"
            data-testid={NAV.logo}
            className="flex items-center group shrink-0"
            aria-label={ariaLabel}
          >
            <Logo size={28} className="shrink-0 transition-transform group-hover:-rotate-2" />
          </Link>

          {/* Middle: wordmark, visually centred between the logo and the
              nav / hamburger cluster. Uses flex-1 to consume all remaining
              space and centre its child on every viewport. */}
          <div className="flex flex-1 justify-center min-w-0">
            <span
              aria-hidden="true"
              className="block truncate text-center text-[13px] sm:text-[15px] lg:text-[18px] font-semibold text-[#0a0a0a] tracking-[-0.01em]"
              data-testid="site-display-name"
            >
              {displayName}
            </span>
          </div>

          {/* Right: nav + CTA (large desktop) or hamburger (mobile/tablet) */}
          <div className="flex items-center gap-5 lg:gap-6 shrink-0">
            <nav className="hidden lg:flex items-center gap-5 lg:gap-6">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={l.tid}
                  end={l.to === "/"}
                  className={({ isActive }) =>
                    `text-[13px] transition-colors ${
                      isActive
                        ? "text-[#1D1D1F]"
                        : "text-[#6E6E73] hover:text-[#1D1D1F]"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
            </nav>

            <Link
              to="/contact"
              data-testid={NAV.ctaStart}
              onClick={() => trackCtaClick("Start a Project", "nav-header")}
              className="hidden lg:inline-flex items-center rounded-full bg-[#1D1D1F] px-4 py-2 text-[13px] font-medium text-white hover:bg-black transition-colors"
            >
              Start a Project
            </Link>

            <button
              type="button"
              className="lg:hidden inline-flex items-center justify-center rounded-md p-2 text-[#1D1D1F]"
              aria-label="Toggle navigation"
              data-testid={NAV.mobileToggle}
              onClick={() => setOpen((v) => !v)}
            >
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[#e5e5ea] bg-white">
          <div className="max-w-[1280px] mx-auto px-6 py-4 flex flex-col gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`${l.tid}-mobile`}
                end={l.to === "/"}
                className={({ isActive }) =>
                  `py-2 text-[15px] ${
                    isActive
                      ? "text-[#1D1D1F] font-medium"
                      : "text-[#424245]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              className="mt-3 inline-flex items-center justify-center rounded-full bg-[#1D1D1F] px-4 py-2 text-[14px] font-medium text-white"
              data-testid={`${NAV.ctaStart}-mobile`}
              onClick={() => trackCtaClick("Start a Project", "nav-mobile")}
            >
              Start a Project
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
